// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from "@mui/material/Grid";
import Checkbox from '@mui/material/Checkbox';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import WYSIWYGEditor from "../../../../../shared-components/WYSIWYGEditor";
import Typography from "@mui/material/Typography";
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { formatISO } from 'date-fns';
// custom components
import NumberedHeading from '../../../../../shared-components/forms/NumberedHeading';

const ReadingAttribution = ({control, errors, inputDir, targetObj}) => {

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent className="py-0">
            <div>
            <NumberedHeading number='1' title="Title (TL)" instructions='Provide a title in TL of the reading' required />
              <Controller 
                control={control}
                name={`${targetObj}.title_tl`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mt-16 mb-16"
                    label="Title (TL)"
                    placeholder="Title (TL)"
                    error={!!errors?.title_tl}
                    helperText={errors?.title_tl?.message}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    required
                    dir={inputDir}
                    size="small"
                  />
                )}
              />
              
              <NumberedHeading number='2' title="Title (EN)" instructions='Provide a title in EN of the reading' required />
              <Controller 
                control={control}
                name={`${targetObj}.title_en`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mt-16 mb-16"
                    label="Title (EN)"
                    placeholder="Title (EN)"
                    error={!!errors?.title_en}
                    helperText={errors?.title_en?.message}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    required
                    size="small"
                  />
                )}
              />

              <NumberedHeading number='3' title="Link" instructions='Provide a link of the reading' required />
              <Controller 
                control={control}
                name={`${targetObj}.source_url`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mt-16 mb-16"
                    label="Link"
                    placeholder="Link"
                    error={!!errors?.source_url}
                    helperText={errors?.source_url?.message}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    required
                    size="small"
                  />
                )}
              />

              <NumberedHeading number='4' title="Content" instructions='Provide content to display of the reading' required />
              <Controller
                control={control}
                name={`${targetObj}.display_content`}
                render={({ field }) => <WYSIWYGEditor {...field} error={errors ? errors.display_content : undefined} />}
              />
              
              
              
              
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <NumberedHeading number='5' title="Published Date" instructions='Provide a published date of the reading' required />
                  <Controller
                    control={control}
                    name={`${targetObj}.published_date`}
                    render={({ field: { onChange, value } }) => (
                      <MobileDatePicker
                        value={value}
                        onChange={(date) => onChange(date ? formatISO(date, { fractionDigits: 6 }) : null)}
                        label="Published Date"
                        renderInput={(_props) => (
                          <TextField label="Publish Date" className="w-full" size="small" {..._props} />
                        )}
                        format="yyyy-MM-dd HH:mm:ss.SSSSSS"
                        className="w-full mt-16"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <NumberedHeading number='6' title="Downloaded Date" instructions='Provide a downloaded date of the reading' required />
                  <Controller
                    control={control}
                    name={`${targetObj}.downloaded_date`}
                    render={({ field: { onChange, value } }) => (
                      <MobileDatePicker
                        value={value}
                        onChange={(date) => onChange(date ? formatISO(date, { fractionDigits: 6 }) : null)}
                        label="Downloaded Date"
                        renderInput={(_props) => (
                          <TextField label="Download Date" className="w-full" size="small" {..._props} />
                        )}
                        format="yyyy-MM-dd HH:mm:ss.SSSSSS"
                        className="w-full mt-16"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <NumberedHeading number='7' title="CC License" instructions='Provide the CC license of the reading' required />
                  <Controller 
                    control={control}
                    name={`${targetObj}.cc_license`}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-16 mb-16"
                        label="CC License"
                        placeholder="CC License"
                        error={!!errors?.cc_license}
                        helperText={errors?.cc_license?.message}
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        required
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <NumberedHeading number='8' title="Author" instructions='Provide an author of the reading' required />
                  <Controller 
                    control={control}
                    name={`${targetObj}.author`}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-16 mb-16"
                        label="Author"
                        placeholder="Author"
                        error={!!errors?.author}
                        helperText={errors?.author?.message}
                        fullWidth
                        required
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <NumberedHeading number='9' title="About the Author (TL)" instructions='Provide a brief description about the author of the reading' />
              <Controller 
                control={control}
                name={`${targetObj}.author_desc_tl`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mt-16 mb-16"
                    label="Author Description (TL)"
                    placeholder="Author Description (TL)"
                    error={!!errors?.author_desc_tl}
                    helperText={errors?.author_desc_tl?.message}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    dir={inputDir}
                  />
                )}
              />

              <NumberedHeading number='10' title="About the Author (EN)" instructions='Provide a brief description about the author of the reading' />
              <Controller 
                control={control}
                name={`${targetObj}.author_desc_en`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mt-16 mb-16"
                    label="Author Description (EN)"
                    placeholder="Author Description (EN)"
                    error={!!errors?.author_desc_en}
                    helperText={errors?.author_desc_en?.message}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                  />
                )}
              />
              
              
            </div>
          </TimelineContent>
        </TimelineItem>
      </Timeline>
    </>
  );
}

export default ReadingAttribution;